import { useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { downloadQRCodeAsPNG } from "@/utils/qrCodeDownload";
import { useToast } from "@/hooks/use-toast";

interface ShowcaseQRCodeProps {
  organizationId: string;
  organizationName?: string;
  size?: number;
}

export const ShowcaseQRCode = ({ 
  organizationId, 
  organizationName = "organization", 
  size = 256 
}: ShowcaseQRCodeProps) => {
  const qrRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  
  // organizationId can now be either a UUID or a slug
  const showcaseUrl = `${window.location.origin}/showcase/${organizationId}`;

  const handleDownload = async () => {
    try {
      const svgElement = qrRef.current?.querySelector('svg');
      if (!svgElement) {
        throw new Error('QR code not found');
      }

      await downloadQRCodeAsPNG(svgElement, organizationName, 512);
      
      toast({
        title: "QR Code Downloaded",
        description: "High-quality PNG saved successfully",
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download Failed",
        description: "Could not download QR code. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col items-center gap-3 p-4 bg-background border border-border rounded-lg">
      <div ref={qrRef}>
        <QRCodeSVG 
          value={showcaseUrl}
          size={size}
          level="M"
          includeMargin={true}
          bgColor="#ffffff"
          fgColor="#000000"
        />
      </div>
      <Button 
        variant="outline" 
        size="sm"
        onClick={handleDownload}
        className="w-full"
      >
        <Download className="h-4 w-4 mr-2" />
        Download QR Code
      </Button>
    </div>
  );
};
